<template>
  <c-box v-if="user && user.partnerId && supportRequest" :mt="{ base: '2.6rem', md: '4xl' }">
    <c-flex
      :flex-direction="{ base: 'column-reverse', md: 'row' }"
      :justify="{ base: 'flex-start', md: 'space-between' }"
      :align="{ base: 'flex-start', md: 'center' }"
      wrap="nowrap"
      w="full"
    >
      <c-flex
        align-self="flex-start"
        :mb="{ base: 'xs', md: 'md' }"
        :mr="{ base: '0', md: '2xl' }"
        :font-size="{ base: '3xl', md: '4xl', xl: '5xl' }"
      >
        <c-heading as="h1">{{ supportRequest.subject }}</c-heading>
      </c-flex>
      <c-box align-self="flex-start" :mb="{ base: 'xl', md: '0' }">
        <router-link to="/support/requests" data-cy="createNewSupportRequest">
          <c-button
            m="0 auto"
            variation="solid"
            background="brand.beige"
            border-color="brand.beige"
            tabindex="-1"
          >
            <c-icon name="chevron-left" size="1.5rem" mr="xs" ml="-sm" />
            {{ $t("tickets.allTickets") }}
          </c-button>
        </router-link>
      </c-box>
    </c-flex>

    <c-box :font-size="{ base: 'xs', md: 'sm' }" :mb="{ base: '2xl', md: '63px' }">
      {{ $t("tickets.ticket") }}
      <c-text as="span" font-weight="bold">{{ supportRequest.id }}</c-text>
    </c-box>

    <c-list>
      <c-list-item class="list_item" data-cy="initialMessage">
        <SupportRequestChatCard
          :conversation-item="{
            ...supportRequest,
            bodyHtml: supportRequest.description,
          }"
          :should-highlight-message="wasSentByUsersOrganisation(supportRequest.email)"
          :is-current-users-message="isUser(supportRequest.email)"
        />
      </c-list-item>

      <c-list-item
        v-for="(conversationItem, index) in conversation"
        :key="conversationItem.id"
        :data-cy="'message_' + index"
        :class="`list_item ${
          wasSentByUsersOrganisation(conversationItem.email) ? 'align-self-end' : 'align-self-start'
        }`"
      >
        <SupportRequestChatCard
          :conversation-item="conversationItem"
          :should-highlight-message="wasSentByUsersOrganisation(conversationItem.email)"
          :is-current-users-message="isUser(conversationItem.email)"
        />
      </c-list-item>
    </c-list>

    <c-flex v-if="conversationLoading" align="center" justify="center" height="40px" mt="-40px">
      <c-box d="inline-block" bg="brand.beigeLight" px="md" py="3xs" border-radius="input">
        {{ $t("tickets.loadingConversation") }}
      </c-box>
    </c-flex>

    <c-box
      position="sticky"
      bottom="0px"
      min-height="80px"
      pt="md"
      z-index="1"
      bg="white"
      border-top="1px solid #ddd"
      :mx="{ base: '-31px', md: '-45px' }"
    >
      <c-box
        v-if="supportRequest.status === TicketStatus.Closed"
        :mx="{ base: '3xl', md: '60px' }"
        mt="sm"
      >
        <c-text>{{ $t("tickets.ticketIsClosedCannotReply") }}</c-text>
      </c-box>
      <FileUpload v-else :key="fileUploadKey" :get-attachments="getAttachments">
        <template v-slot:fileUploadArea="slotProps">
          <c-box @click.stop>
            <c-flex flex-direction="row">
              <c-flex
                :ml="{ base: 'lg', md: '3xl' }"
                :mr="{ base: 'sm', md: 'md' }"
                mt="2xs"
                color="gray.700"
                :_hover="{
                  cursor: 'pointer',
                  color: 'brand.blueLight',
                }"
                @click="slotProps.openFileUpload"
                @keydown.enter="slotProps.openFileUpload"
                tabindex="0"
              >
                <c-icon name="paperclip" size="2rem" />
              </c-flex>
              <c-flex flex-grow="1" max-width="870px" :mr="{ base: 'lg', md: 'xl' }">
                <c-textarea
                  background-image="url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0nMTQnIGhlaWdodD0nMTMnIHZpZXdCb3g9JzAgMCAxNCAxMycgZmlsbD0nbm9uZScgeG1sbnM9J2h0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnJz48cGF0aCBkPSdNMTAuNSA4Ljk5OTk5TDEzLjUgOS4wMDAwMVYwSDBWOC45OTk5OUg2LjQzOTM0TDEwLjUgMTIuNzVMMTEuMjUgMTEuMjVMNy4wNjA2NiA3LjQ5OTk5SDEuNVYxLjVIMTJWNy41TDEwLjUgNy40OTk5OUwxMC41IDguOTk5OTlaJyBmaWxsPScjQkVCRUJFJy8+PHBhdGggZD0nTTMgNS4yNUg0LjVWMy43NUgzVjUuMjVaJyBmaWxsPScjQkVCRUJFJy8+PHBhdGggZD0nTTcuNSA1LjI1SDZWMy43NUg3LjVWNS4yNVonIGZpbGw9JyNCRUJFQkUnLz48cGF0aCBkPSdNOSA1LjI1SDEwLjVWMy43NUg5VjUuMjVaJyBmaWxsPScjQkVCRUJFJy8+PC9zdmc+')"
                  background-position="14px 14px"
                  background-repeat="no-repeat"
                  p="12px 12px 12px 42px"
                  font-size="sm"
                  :placeholder="$t('tickets.reply.placeholder')"
                  v-model="message"
                  :h="message.length === 0 ? '40px' : '80px'"
                  :overflow="message.length === 0 ? 'hidden' : 'auto'"
                  min-height="40px"
                  resize="none"
                  :disabled="submittingReply"
                />
              </c-flex>

              <c-flex>
                <c-button
                  :mr="{ base: 'md', md: '3xl' }"
                  :pl="{ base: 'xl', md: '2xl' }"
                  :pr="{ base: 'xl', md: '2xl' }"
                  variation="solid"
                  background="brand.beige"
                  border-color="brand.beige"
                  tabindex="0"
                  @click="submittingReply || sendReply()"
                >
                  <Spinner v-if="submittingReply" v-chakra mx="-md" />
                  <c-icon v-else name="paper_plane" size="2rem" mx="-md" />
                </c-button>
              </c-flex>
            </c-flex>

            <c-flex wrap="wrap" mt="lg" pb="2xs" :px="{ base: 'xl', md: '3xl' }">
              <c-box
                v-for="(attachment, index) in slotProps.attachments"
                :key="attachment.name"
                font-size="sm"
                line-height="1.2"
                height="auto"
                border-radius="input"
                bg="brand.gray"
                mx="2xs"
                mb="xs"
                font-weight="semibold"
              >
                <c-flex align="center" justify="space-between" pl="md" pr="2xs" my="-2px">
                  <c-text my="2xs" as="span" font-size="xs">
                    {{ attachment.name }}
                  </c-text>
                  <c-flex
                    align="center"
                    justify="center"
                    ml="xs"
                    mr="2xs"
                    :_hover="{
                      color: 'brand.blueLight',
                      cursor: 'pointer',
                    }"
                    tabindex="0"
                    @click="() => slotProps.removeFile(index)"
                    @keydown.enter="() => slotProps.removeFile(index)"
                  >
                    <c-icon name="cross" size="1rem" />
                  </c-flex>
                </c-flex>
              </c-box>
            </c-flex>
          </c-box>
        </template>
        <template v-slot:fileListing>
          <!-- Prevent default slot from showing up -->
          <span />
        </template>
      </FileUpload>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import FileUpload from "@/components/FileUpload.vue";
import SearchInput from "@/components/SearchInput.vue";
import Spinner from "@/components/Spinner.vue";
import Button from "@/design-system/elements/Button.vue";
import ButtonGroup from "@/design-system/elements/ButtonGroup.vue";
import Card from "@/design-system/elements/Card.vue";
import Icon from "@/design-system/elements/Icon.vue";
import { ISolutionsState } from "@/store/solutionsModule";
import SupportRequestChatCard from "@/views/SupportRequestChatCard.vue";
import {
  CBox,
  CButton,
  CFlex,
  CHeading,
  CIcon,
  CList,
  CListItem,
  CText,
  CTextarea,
} from "@chakra-ui/vue";
import {
  ISupportRequest,
  ISupportRequestConversationItem,
  ISupportRequestConversationItemInput,
  TicketStatus,
} from "@iloq/shared";
import { last } from "lodash";
import { defineComponent } from "vue";
import { mapState } from "vuex";

export default defineComponent({
  name: "SupportRequestChat",
  components: {
    Spinner,
    CBox,
    CButton,
    CFlex,
    CHeading,
    CIcon,
    CList,
    CListItem,
    CText,
    CTextarea,
    SearchInput,
    ButtonGroup,
    Card,
    Button,
    Icon,
    SupportRequestChatCard,
    FileUpload,
  },
  data(): any {
    return {
      message: "",
      attachments: [] as File[],
      conversation: [] as ISupportRequestConversationItem[],
      conversationLoading: false,
      fileUploadKey: Math.random(),
      submittingReply: false,
      timer: 0,
    };
  },
  computed: {
    TicketStatus(): typeof TicketStatus {
      return TicketStatus;
    },
    ...mapState("solutions", {
      supportRequest: function (state: any): ISupportRequest | undefined {
        return (state as ISolutionsState).supportRequests?.values.find(
          (request) => String(request.id) === String((this as any).$route.params.id)
        );
      },
    }),
    ...(mapState(["user"]) as any),
  },
  watch: {
    "user.partnerId"() {
      this.fetchInitialConversationFromTicket();
    },
  },
  mounted() {
    this.fetchInitialConversationFromTicket();
  },
  beforeDestroy() {
    window.clearTimeout(this.timer);
  },
  methods: {
    wasSentByUsersOrganisation(email = ""): boolean {
      const { email: userEmail } = this.user;

      const currentUserIsSupportRep = userEmail.includes("@iloq.");
      const senderIsSupportRep = email.includes("@iloq.");

      if (currentUserIsSupportRep === senderIsSupportRep) {
        // Since conversation messages don't have partnerId information, we need to try and group the messages by partner some other way.

        // If current user and ticket sender are both iloq support reps current message needs to highlighted.
        // Also if neither current user nor ticket sender are iloq support reps message needs to highlighted as well.

        // Not a particularly versatile way of grouping messages by partner, but seems to work.
        return true;
      }
      return false;
    },
    isUser(email = ""): boolean {
      const { email: userEmail } = this.user;
      return email === userEmail;
    },
    getConversationUpdating(): boolean {
      return this.conversationLoading && this.conversation?.length;
    },
    async fetchInitialConversationFromTicket() {
      // User is enriched with partnerId sometime after they've logged in. Need to make sure we don't try this before that has happened
      if (this.user.partnerId) {
        // There isn't a conversation for this ticket in Vuex state. Possible reasons for this are
        // 1. User clicked refresh and store hasn't been populated with tickets
        // 2. There really is no conversation
        if (!this.supportRequest?.conversation) {
          // Get ticket with this id from backend, see if there's a conversation there
          await this.$store.dispatch("solutions/fetchSupportRequests", {
            id: this.$route.params.id,
            pageState: {
              top: 1,
              skip: 0,
            },
          });
          if (this.supportRequest?.conversation) {
            this.conversation = this.supportRequest?.conversation;
          } else {
            // There really was no conversation to display <o>
          }
        } else {
          // There was a conversation in VueX store. Put that in local state
          this.conversation = this.supportRequest?.conversation;
        }

        await this.fetchConversation(); // Refresh the conversation immediately from Freshdesk (above data comes from search index and may not be in sync)
        this.scrollToBottom();
      }
    },
    async sendReply() {
      const payload: ISupportRequestConversationItemInput = {
        message: this.message,
        name: `${this.user.firstName} ${this.user.lastName}`,
        attachments: this.attachments,
        ticketId: this.$route.params.id,
      };

      if (payload.message || payload.attachments?.length) {
        this.submittingReply = true;
        try {
          const updatedTicket = await this.$store.dispatch("solutions/submitReply", payload);
          if (updatedTicket) {
            this.conversation = updatedTicket.conversation;
          }
          this.submittingReply = false;
          this.message = "";
          this.attachments = [];
          this.fileUploadKey = Math.random(); // Hax to the max: force child component update so attachment list gets reset
          this.scrollToBottom();
        } catch (e) {
          this.$store.commit("showGeneralError", {
            dialogTitle: this.$t("tickets.submitReplyFailed.title"),
            dialogText: this.$t("tickets.submitReplyFailed.message"),
          });
          this.submittingReply = false;
        }
      }
    },
    async fetchConversation() {
      const removeDuplicateMessages = (
        conversation: ISupportRequestConversationItem[]
      ): ISupportRequestConversationItem[] => {
        const messageIds: { [key: number]: boolean } = {};
        return conversation.filter((item) => {
          if (messageIds[item.id]) {
            return false;
          }
          messageIds[item.id] = true;
          return true;
        });
      };

      if (!this.submittingReply) {
        this.conversationLoading = true;
        const afterDate =
          (last(this.conversation) as any)?.updatedAt ?? this.supportRequest?.updatedAt;
        const conversation = await this.$store.dispatch("solutions/fetchConversation", {
          ticketId: this.$route.params.id,
          afterDate: new Date(afterDate),
          partnerId: this.user.partnerId,
        });
        if (conversation && conversation.length) {
          // In some rare cases duplicates may occur (when reply sending and conversation polling happen very close to each other)
          this.conversation = removeDuplicateMessages(this.conversation.concat(conversation));
        }
        this.conversationLoading = false;
      }

      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(this.fetchConversation, 1000 * 30); // Wait 30 secs before refreshing chat
    },
    getAttachments(files: File[]) {
      this.attachments = files;
    },
    scrollToBottom() {
      setTimeout(() => {
        window.scrollTo(0, document.body.scrollHeight);
      }, 1); // Wait for DOM to update before scrolling to bottom
    },
  },
});
</script>

<style lang="scss">
.support_request_details .wrapper {
  padding-bottom: 0;
}
</style>
